import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Fade,
  Slide,
  LinearProgress
} from "@mui/material";
import { 
  VerifiedUser, 
  Star,
  Shield
} from "@mui/icons-material";

// 💎 Estilos Clean Corporate padronizados
const cleanInputStyles = {
  input: {
    color: "#1E293B",
    fontWeight: "500",
    fontSize: "1rem",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#F8FAFC",
    border: "1px solid transparent",
    transition: "all 0.2s ease-in-out",
    "& fieldset": { borderColor: "#E2E8F0", transition: "all 0.2s ease-in-out" },
    "&:hover fieldset": { borderColor: "#CBD5E1" },
    "&.Mui-focused fieldset": { borderColor: "#DAA520", borderWidth: "2px" },
    "&.Mui-focused": { backgroundColor: "#FFFFFF", boxShadow: "0 4px 12px rgba(218, 165, 32, 0.08)" },
    "&.Mui-disabled": { backgroundColor: "#F1F5F9" },
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" }
  },
  "& .MuiInputLabel-root": { color: "#64748B", fontWeight: "500" },
  "& .Mui-disabled": { WebkitTextFillColor: "#94A3B8", color: "#94A3B8" }
};

const ValidateResetCode = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const emailFromUrl = queryParams.get("email");

  const [email, setEmail] = useState(emailFromUrl || location.state?.email || "");
  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const impactImages = [
    "/images/carousel_image_1.jpg",
    "/images/carousel_image_2.jpg", 
    "/images/carousel_image_3.jpg"
  ];

  const LOGO_EMPRESA_URL = "/images/logo_dourado.png";

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === impactImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);
    return () => clearInterval(interval);
  }, [impactImages.length]);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(timer);
          return 100;
        }
        return prevProgress + 2;
      });
    }, 80);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!email) {
      console.warn("⚠️ E-mail ausente! O usuário não poderá validar o código.");
      setErrorMessage("⚠️ Erro ao carregar seus dados. Retorne à recuperação de senha.");
      setTimeout(() => {
        navigate("/Security/recuperar-senha", { replace: true });
      }, 3000);
    }
  }, [email, navigate]);

  const handleValidateCode = async (e) => {
    if(e) e.preventDefault();
    const formattedCode = code.trim();

    if (!formattedCode) {
      setErrorMessage("⚠️ Por favor, insira o código recebido por e-mail.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const response = await api.post("/api/Usuario/validate-reset-code", {
        email,
        code: formattedCode,
      });

      navigate("/Security/reset-password", { state: { email, code: formattedCode } });
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "⚠️ Código inválido ou expirado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", position: "relative", overflow: "hidden", backgroundColor: "#FFFFFF" }}>
      {/* Área de Impacto Visual - 70% */}
      <Box sx={{ width: { xs: "100%", md: "70%" }, position: "relative", display: { xs: "none", md: "block" }, background: `linear-gradient(135deg, rgba(184, 134, 11, 0.95) 0%, rgba(218, 165, 32, 0.9) 25%, rgba(205, 127, 50, 0.9) 50%, rgba(139, 105, 20, 0.95) 75%, rgba(112, 66, 20, 0.98) 100%)`, overflow: "hidden" }}>
        <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 1 }}>
          {impactImages.map((image, index) => (
            <Box key={index} sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", backgroundImage: `url(${image})`, backgroundSize: "cover", backgroundPosition: "center", opacity: currentImageIndex === index ? 1 : 0, transition: "opacity 2s ease-in-out", zIndex: 1 }} />
          ))}
        </Box>
        <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", background: `linear-gradient(135deg, rgba(184, 134, 11, 0.85) 0%, rgba(218, 165, 32, 0.75) 25%, rgba(205, 127, 50, 0.75) 50%, rgba(139, 105, 20, 0.85) 75%, rgba(112, 66, 20, 0.9) 100%)`, zIndex: 2 }} />
        
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", color: "white", zIndex: 4, maxWidth: "85%" }}>
          <Fade in={true} timeout={1000}>
            <Box>
              <Box display="flex" justifyContent="center" alignItems="center" mb={3}>
                <Shield sx={{ fontSize: "4rem", mr: 2, color: "#FFD700" }} />
                <VerifiedUser sx={{ fontSize: "3.5rem", mr: 2, color: "#DAA520" }} />
                <Star sx={{ fontSize: "4rem", color: "#FFD700" }} />
              </Box>
              <Typography variant="h1" sx={{ fontWeight: "900", mb: 3, fontSize: { xs: "2.5rem", md: "4.5rem" }, textShadow: "4px 4px 8px rgba(0,0,0,0.8)", fontFamily: "'Playfair Display', serif", letterSpacing: "3px", background: "linear-gradient(45deg, #FFD700, #FFA500, #FFD700)", backgroundClip: "text", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                VALIDAÇÃO SEGURA
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: "700", mb: 4, fontSize: { xs: "1.5rem", md: "2.5rem" }, textShadow: "3px 3px 6px rgba(0,0,0,0.7)", fontFamily: "'Roboto', sans-serif", letterSpacing: "2px" }}>
                ACESSO PROTEGIDO
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, fontSize: { xs: "1rem", md: "1.5rem" }, textShadow: "2px 2px 4px rgba(0,0,0,0.6)", opacity: 0.95, fontFamily: "'Roboto', sans-serif", fontWeight: "400", letterSpacing: "1px" }}>
                Segurança Máxima • Tecnologia Avançada • Proteção Total
              </Typography>
              <Box sx={{ width: "80%", mx: "auto", mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>Verificando credenciais de segurança...</Typography>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4, backgroundColor: "rgba(255,255,255,0.3)", "& .MuiLinearProgress-bar": { backgroundColor: "#FFD700", borderRadius: 4 } }} />
                <Typography variant="caption" sx={{ mt: 1, display: "block" }}>{progress}% Verificado</Typography>
              </Box>
            </Box>
          </Fade>
        </Box>
      </Box>

      {/* Área do Formulário - 30% */}
      <Box sx={{ width: { xs: "100%", md: "30%" }, display: "flex", justifyContent: "center", alignItems: "center", position: "relative", minHeight: "100vh", backgroundColor: "#FFFFFF", zIndex: 10, boxShadow: "-10px 0 30px rgba(0,0,0,0.06)" }}>
        <Slide direction="left" in={true} timeout={800}>
          <Box sx={{ width: "100%", maxWidth: "400px", p: { xs: 4, md: 5 }, display: "flex", flexDirection: "column", alignItems: "center" }}>
            
            <Box textAlign="center" mb={4} width="100%">
              <img src={LOGO_EMPRESA_URL} alt="Logo" style={{ width: "140px", marginBottom: "20px", marginLeft: "auto", marginRight: "auto", display: "block" }} />
              <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                <VerifiedUser sx={{ color: "#DAA520", mr: 1, fontSize: "1.8rem" }} />
                <Typography variant="h5" sx={{ fontWeight: "700", color: "#1E293B", fontFamily: "'Playfair Display', serif", letterSpacing: "0.5px" }}>
                  Validar Código
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: "#64748B", fontFamily: "'Roboto', sans-serif" }}>
                Digite o código de segurança enviado
              </Typography>
            </Box>

            <Box sx={{ width: "100%", minHeight: "60px", mb: 1 }}>
              <Fade in={!!errorMessage}>
                <Box sx={{ display: errorMessage ? "block" : "none", backgroundColor: "#FEF2F2", color: "#991B1B", borderRadius: "8px", padding: "12px", textAlign: "center", border: "1px solid #FCA5A5", fontWeight: "500", fontSize: "0.9rem" }}>
                  {errorMessage}
                </Box>
              </Fade>
            </Box>

            <form style={{ width: "100%" }} onSubmit={handleValidateCode}>
              <TextField 
                fullWidth 
                label="E-mail de destino" 
                variant="outlined" 
                margin="normal" 
                value={email} 
                disabled={true} 
                sx={{
                  ...cleanInputStyles,
                  "& .MuiOutlinedInput-input": { textAlign: "center", color: "#94A3B8" }
                }} 
              />

              <TextField 
                fullWidth 
                label="Código de Segurança" 
                variant="outlined" 
                margin="normal" 
                type="text" 
                value={code} 
                onChange={(e) => setCode(e.target.value)} 
                placeholder="Ex: 123456" 
                disabled={loading} 
                inputProps={{ maxLength: 6 }}
                sx={{
                  ...cleanInputStyles,
                  "& .MuiOutlinedInput-input": { 
                    textAlign: "center", 
                    letterSpacing: "4px", 
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    color: "#1E293B"
                  }
                }} 
              />

              <Button 
                fullWidth 
                type="submit"
                variant="contained" 
                disableElevation
                sx={{ 
                  mt: 3, 
                  py: 1.5,
                  borderRadius: "12px",
                  backgroundColor: "#DAA520",
                  color: "#FFFFFF", 
                  fontWeight: "bold",
                  fontSize: "1rem",
                  textTransform: "none",
                  letterSpacing: "0.5px",
                  "&:hover": { 
                    backgroundColor: "#B8860B",
                    transform: "translateY(-1px)",
                    boxShadow: "0 8px 20px rgba(218, 165, 32, 0.3)"
                  },
                  "&.Mui-disabled": { backgroundColor: "#E2E8F0", color: "#94A3B8" }
                }} 
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} sx={{ color: "#FFFFFF" }} /> : "VALIDAR CÓDIGO"}
              </Button>

              <Button 
                fullWidth 
                type="button"
                sx={{ 
                  mt: 2, 
                  py: 1.2,
                  borderRadius: "8px",
                  color: "#64748B",
                  fontWeight: "500",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#F8FAFC", color: "#DAA520" }
                }} 
                onClick={() => navigate("/login")}
                disabled={loading}
              >
                Cancelar e voltar
              </Button>
            </form>
          </Box>
        </Slide>
      </Box>
    </Box>
  );
};

export default ValidateResetCode;